import React, { useEffect, useState } from 'react';
import { Button, List, Tag, Empty, Space, Typography, Modal } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { ProcedurePicker } from './ProcedurePicker';
import { DiagnosticForm } from './DiagnosticForm';
import { DiagnosticDetail } from './DiagnosticDetail';
import { diagnosticService } from '../../services/diagnosticService';
import type { Run, Procedure, VehicleSnapshot } from '../../services/diagnosticService';

const { Text } = Typography;

export interface DiagnosticsTabProps {
  workOrderId: number;
  vehicle?: VehicleSnapshot;
  /** Optional: when set, opens the ProcedurePicker pre-linked to a specific WO line. */
  prelinkLineItemId?: number | null;
  onPrelinkConsumed?: () => void;
}

export const DiagnosticsTab: React.FC<DiagnosticsTabProps> = ({
  workOrderId,
  vehicle,
  prelinkLineItemId,
  onPrelinkConsumed,
}) => {
  const [runs, setRuns] = useState<Run[]>([]);
  const [procedures, setProcedures] = useState<Procedure[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerLineId, setPickerLineId] = useState<number | null>(null);
  const [activeRun, setActiveRun] = useState<Run | null>(null);
  const [activeProcedure, setActiveProcedure] = useState<Procedure | null>(null);

  const reload = () =>
    diagnosticService.listForWorkOrder(workOrderId).then(({ data }) => setRuns(data.runs));

  useEffect(() => {
    reload();
    diagnosticService.listProcedures().then(({ data }) => setProcedures(data.procedures));
  }, [workOrderId]); // eslint-disable-line react-hooks/exhaustive-deps

  // If the parent passed a prelink, open the picker with that line id.
  useEffect(() => {
    if (prelinkLineItemId != null) {
      setPickerLineId(prelinkLineItemId);
      setPickerOpen(true);
      onPrelinkConsumed?.();
    }
  }, [prelinkLineItemId, onPrelinkConsumed]);

  const handlePick = async (p: Procedure) => {
    setPickerOpen(false);
    const { data } = await diagnosticService.createRun(workOrderId, {
      procedureId: p.id,
      workOrderLineId: pickerLineId ?? undefined,
    });
    setPickerLineId(null);
    const { data: full } = await diagnosticService.getRun(data.id);
    setActiveProcedure(p);
    setActiveRun(full);
    await reload();
  };

  const handleRowClick = async (r: Run) => {
    const proc = procedures.find((p) => p.id === r.procedure_id) ?? null;
    setActiveProcedure(proc);
    setActiveRun(r);
  };

  return (
    <div>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 12 }}>
        <Text strong>Diagnostics</Text>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => { setPickerLineId(null); setPickerOpen(true); }}
        >
          New diagnostic
        </Button>
      </Space>

      {runs.length === 0 ? (
        <Empty description="No diagnostics yet" />
      ) : (
        <List
          dataSource={runs}
          renderItem={(r) => {
            const proc = procedures.find((p) => p.id === r.procedure_id);
            return (
              <List.Item onClick={() => handleRowClick(r)} style={{ cursor: 'pointer' }}>
                <List.Item.Meta
                  title={
                    <Space>
                      <Text strong>{proc?.name ?? `procedure #${r.procedure_id}`}</Text>
                      {r.overall_verdict && (
                        <Tag
                          color={
                            r.overall_verdict === 'green'
                              ? 'success'
                              : r.overall_verdict === 'yellow'
                              ? 'warning'
                              : 'error'
                          }
                        >
                          {r.overall_verdict.toUpperCase()}
                        </Tag>
                      )}
                      <Tag>{r.status}</Tag>
                    </Space>
                  }
                  description={<Text type="secondary">{r.created_at}</Text>}
                />
              </List.Item>
            );
          }}
        />
      )}

      <ProcedurePicker
        open={pickerOpen}
        onClose={() => { setPickerOpen(false); setPickerLineId(null); }}
        onPick={handlePick}
        vehicle={vehicle}
      />

      <Modal
        open={activeRun !== null}
        onCancel={() => { setActiveRun(null); setActiveProcedure(null); reload(); }}
        footer={null}
        width={800}
        destroyOnClose
        title={activeProcedure?.name ?? 'Diagnostic'}
      >
        {activeRun &&
          activeProcedure &&
          (activeRun.status === 'completed' ? (
            <DiagnosticDetail run={activeRun} procedure={activeProcedure} />
          ) : (
            <DiagnosticForm
              run={activeRun}
              procedure={activeProcedure}
              onCompleted={() => {
                setActiveRun(null);
                setActiveProcedure(null);
                reload();
              }}
            />
          ))}
      </Modal>
    </div>
  );
};
